import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CUISINE_INTERESTS,
  EVENT_TYPES,
  BUDGETS,
} from "@/lib/constants";

// Quote requests are emailed directly via FormSubmit — no backend required.
const FORM_ENDPOINT =
  "https://formsubmit.co/ajax/heritagetablecatering@gmail.com";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  event_date: "",
  event_location: "",
  event_type: "",
  guest_count: "",
  cuisine_interests: [],
  dessert_addon: false,
  budget_range: "",
  message: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleCuisine = (c) =>
    setForm((f) => ({
      ...f,
      cuisine_interests: f.cuisine_interests.includes(c)
        ? f.cuisine_interests.filter((x) => x !== c)
        : [...f.cuisine_interests, c],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        guest_count: form.guest_count ? Number(form.guest_count) : null,
        cuisine_interests: form.cuisine_interests.join(", "),
        _subject: "New Quote Request — Heritage Table Catering",
        _template: "table",
      };
      await axios.post(FORM_ENDPOINT, payload);
      toast.success(
        "Thank you — your request is at our table. We'll be in touch shortly."
      );
      setSubmitted(true);
      setForm(emptyForm);
    } catch (err) {
      console.error(err);
      toast.error(
        "Something went wrong sending your request. Please try again or email us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="section bg-cream-deep relative"
    >
      <div className="mx-auto max-w-4xl">
        <div className="text-center">
          <span className="eyebrow">request a quote</span>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[hsl(var(--brown))]">
            Tell us about{" "}
            <span className="font-script text-[hsl(var(--rose-deep))]">
              your gathering
            </span>
            .
          </h2>
          <p className="mt-4 text-[hsl(var(--brown-soft))]">
            Share a few details and we'll craft a menu that fits your event,
            your guests, and your traditions.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="quote-form"
          className="mt-10 recipe-card p-6 sm:p-10 bg-[hsl(var(--cream))]"
        >
          {submitted && (
            <div
              data-testid="quote-success-banner"
              className="mb-6 rounded-xl border border-[hsl(var(--sage-deep)/0.4)] bg-[hsl(var(--sage)/0.18)] px-4 py-3 text-sm text-[hsl(var(--sage-deep))]"
            >
              Your request has been received. A reply is on its way.
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Full name *">
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="heritage-input"
                data-testid="quote-input-name"
                placeholder="Your name"
              />
            </Field>
            <Field label="Email *">
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="heritage-input"
                data-testid="quote-input-email"
                placeholder="you@email.com"
              />
            </Field>
            <Field label="Phone *">
              <input
                type="tel"
                required
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className="heritage-input"
                data-testid="quote-input-phone"
                placeholder="(407) 555-0123"
              />
            </Field>
            <Field label="Event date">
              <input
                type="date"
                value={form.event_date}
                onChange={(e) => update("event_date", e.target.value)}
                className="heritage-input"
                data-testid="quote-input-date"
              />
            </Field>
            <Field label="Event location">
              <input
                type="text"
                value={form.event_location}
                onChange={(e) => update("event_location", e.target.value)}
                className="heritage-input"
                data-testid="quote-input-location"
                placeholder="City, venue, or address"
              />
            </Field>
            <Field label="Event type">
              <select
                value={form.event_type}
                onChange={(e) => update("event_type", e.target.value)}
                className="heritage-input"
                data-testid="quote-input-event-type"
              >
                <option value="">Select event type</option>
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Estimated guest count">
              <input
                type="number"
                min="0"
                value={form.guest_count}
                onChange={(e) => update("guest_count", e.target.value)}
                className="heritage-input"
                data-testid="quote-input-guests"
                placeholder="e.g. 75"
              />
            </Field>
            <Field label="Budget range">
              <select
                value={form.budget_range}
                onChange={(e) => update("budget_range", e.target.value)}
                className="heritage-input"
                data-testid="quote-input-budget"
              >
                <option value="">Select a range</option>
                {BUDGETS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="mt-7">
            <span className="heritage-label">Cuisine interests</span>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2"
              data-testid="quote-cuisine-grid"
            >
              {CUISINE_INTERESTS.map((c) => {
                const active = form.cuisine_interests.includes(c);
                return (
                  <label
                    key={c}
                    data-testid={`cuisine-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition ${
                      active
                        ? "border-[hsl(var(--rose-deep))] bg-[hsl(var(--rose)/0.15)]"
                        : "border-[hsl(var(--border))] bg-white hover:border-[hsl(var(--rose)/0.6)]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleCuisine(c)}
                      className="h-4 w-4 accent-[hsl(var(--rose-deep))]"
                    />
                    <span className="text-sm text-[hsl(var(--brown))]">
                      {c}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="mt-6">
            <label
              className="flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer bg-[hsl(var(--gold)/0.1)] border-[hsl(var(--gold)/0.4)]"
              data-testid="quote-dessert-addon"
            >
              <input
                type="checkbox"
                checked={form.dessert_addon}
                onChange={(e) => update("dessert_addon", e.target.checked)}
                className="mt-1 h-4 w-4 accent-[hsl(var(--gold-deep))]"
              />
              <span className="text-sm text-[hsl(var(--brown))]">
                <span className="font-semibold">
                  Add a dessert table from Central Florida Custom Cakes.
                </span>
                <span className="block text-xs text-[hsl(var(--brown-soft))] mt-0.5">
                  Custom cakes, pies, cookies, cupcakes, or a full dessert
                  spread.
                </span>
              </span>
            </label>
          </div>

          <div className="mt-6">
            <Field label="Message / event details">
              <textarea
                rows={5}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="heritage-input resize-none"
                data-testid="quote-input-message"
                placeholder="Tell us about your gathering — guests, traditions, dietary needs, themes, or any meaningful details."
              />
            </Field>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-xs text-[hsl(var(--brown-soft))] max-w-sm">
              We typically reply within one to two business days. Your details
              stay with our small team.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="quote-submit-btn"
              className="btn-pill btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Send Quote Request"}
              {!submitting && <span aria-hidden>→</span>}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="heritage-label">{label}</span>
      {children}
    </label>
  );
}
